import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShieldCheck, X } from "lucide-react";
import LiveMark from "@/pages/idevra/LiveMark";
import { sfx } from "@/lib/sound";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminGate() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === "A" || e.key === "a")) {
        e.preventDefault();
        sfx.clank();
        setOpen(true);
        setError("");
        setPassword("");
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 120);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!password || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        sfx.back();
        setError(res.status === 429 ? "CONSOLE LOCKED — TOO MANY ATTEMPTS" : "ACCESS DENIED — INVALID OVERRIDE CODE");
        return;
      }
      sfx.psLaunch();
      localStorage.setItem("idevra_inbox_token", data.token);
      setOpen(false);
      navigate("/inbox");
    } catch {
      setError("LINK FAILURE — TRY AGAIN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          data-testid="admin-gate"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <button
            type="button"
            aria-label="Close"
            data-testid="admin-gate-backdrop"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-[#0F172A]/40 backdrop-blur-md"
          />
          <motion.form
            onSubmit={submit}
            noValidate
            className="iv-glass relative flex w-full max-w-sm flex-col items-center gap-5 rounded-2xl p-8"
            initial={{ scale: 0.92, y: 18 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 10, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
          >
            <button
              type="button"
              data-testid="admin-gate-close"
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-[#CBD5E1] text-[#475569] hover:border-[#38BDF8] hover:text-[#0284C7]"
            >
              <X size={13} />
            </button>
            <LiveMark className="w-12" />
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <ShieldCheck size={14} color="#0284C7" />
                <span className="f-syncopate text-xs tracking-[0.35em] text-[#0F172A]">OPERATOR OVERRIDE</span>
              </div>
              <div className="f-mono mt-2 text-[9px] tracking-[0.25em] text-[#94A3B8]">
                RESTRICTED CHANNEL — ENTER OVERRIDE CODE
              </div>
            </div>
            <input
              ref={inputRef}
              type="password"
              data-testid="admin-password-input"
              className="iv-input"
              placeholder="OVERRIDE CODE"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            {error && (
              <div className="f-mono text-[9px] tracking-[0.15em] text-[#D97706]" data-testid="admin-gate-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              data-testid="admin-gate-submit"
              disabled={loading}
              className="f-syncopate w-full rounded-full bg-[#0284C7] px-6 py-3 text-[10px] tracking-[0.3em] text-white transition-colors hover:bg-[#0369A1] disabled:opacity-60"
            >
              {loading ? "VERIFYING…" : "AUTHORIZE"}
            </button>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
